import { pick } from 'remeda';
import { createContext, useContext, useState, useEffect } from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { RecaptchaBridge } from '../hooks/useRecaptcha.jsx';
import { useLocalStorage } from '../hooks/useApi';
import { apiService } from '../services/api';

const SettingsContext = createContext();

// Only these fields render above the fold on every page (Header/Footer brand,
// global theme CSS vars) before the settings fetch resolves - they're the
// ones a stale/hardcoded default causes a visible flash on. Everything else
// (SEO meta, social links, recaptcha, login menu, ...) isn't rendered until
// later or isn't visual, so it doesn't need to survive a reload.
const FLICKER_CACHED_SETTINGS_KEYS = [
  'site_name',
  'logo_url',
  'logo_dark_url',
  'theme_primary_color',
  'theme_accent_color',
  'theme_highlight_color',
  'theme_neutral_color',
  'theme_heading_font',
  'theme_body_font',
  'theme_border_radius',
];

const FLICKER_CACHED_NAV_KEYS = [
  'text_theme',
  'background_enabled',
  'background_color',
  'background_opacity',
];

/**
 * Tailwind needs theme colours as "R G B" channels to support opacity
 * modifiers, while plain CSS elsewhere uses the hex form. Site settings supply
 * hex, so both are written and kept in step.
 */
function setThemeColor(name, hex) {
  if (!hex) return;
  const root = document.documentElement;
  root.style.setProperty(`--color-${name}`, hex);

  const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(String(hex).trim());
  if (match) {
    const [r, g, b] = match.slice(1).map((part) => parseInt(part, 16));
    root.style.setProperty(`--color-${name}-rgb`, `${r} ${g} ${b}`);
  }
}

export function SettingsProvider({ children }) {
  // Only the flicker-critical fields are cached in localStorage (see
  // FLICKER_CACHED_*_KEYS above) - not the full settings objects - so a
  // returning visitor's last known-good logo/theme render immediately
  // instead of these hardcoded defaults, without persisting fields (SEO,
  // social links, recaptcha, login menu, ...) that never caused a flash.
  const [cachedNavSettings, setCachedNavSettings] = useLocalStorage('cgsnj:cachedNavigationSettings', {});
  const [cachedSettings, setCachedSettings] = useLocalStorage('cgsnj:cachedSettings', {});

  const [navigationSettings, setNavigationSettings] = useState({
    background_enabled: false,
    background_color: '#FFFFFF',
    background_opacity: 1.0,
    text_theme: 'dark',
    login_enabled: true,
    login_url: '/member/login',
    login_menu_links: [],
    ...cachedNavSettings,
  });

  const [settings, setSettings] = useState({
    // General
    site_name: 'The Church Gathered & Scattered',
    site_tagline: 'A Reformed Presbyterian Church in Teaneck, NJ',
    site_url: 'https://cgsnj.org',

    // Branding
    // No default image files ship in public/ for these - leaving them empty
    // (rather than pointing at a path that 404s) lets Header/Footer fall back
    // to their styled placeholder until the real logo loads from settings.
    logo_url: '',
    logo_dark_url: '',
    favicon_url: '/favicon.svg',

    // SEO
    meta_title: 'CGS - The Church Gathered & Scattered',
    meta_description: 'A Reformed Presbyterian church in Teaneck, New Jersey.',
    enable_robots: true,

    // Theme
    theme_primary_color: '#072441',
    theme_accent_color: '#FFE658',
    theme_highlight_color: '#D8EAF8',
    theme_neutral_color: '#E7E4DC',
    theme_heading_font: 'Crimson Pro',
    theme_body_font: 'Open Sans',
    theme_border_radius: '8',

    // Social
    social_facebook: '',
    social_instagram: '',
    social_youtube: '',

    // Integrations
    youtube_channel_id: '',
    google_analytics_id: '',

    // reCAPTCHA
    recaptcha_site_key: '',
    recaptcha_enabled: false,

    ...cachedSettings,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const [response, navFooterResponse] = await Promise.all([
        apiService.getSettings(),
        apiService.getNavigationFooter(),
      ]);

      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
        setCachedSettings(pick(response.data, FLICKER_CACHED_SETTINGS_KEYS));
      }

      if (navFooterResponse.data?.navigationSettings) {
        const navSettings = navFooterResponse.data.navigationSettings;
        let parsedLinks = navSettings.login_menu_links;
        if (typeof parsedLinks === 'string') {
          try { parsedLinks = JSON.parse(parsedLinks); } catch { parsedLinks = []; }
        }
        const resolvedNavSettings = {
          background_enabled: navSettings.background_enabled ?? false,
          background_color: navSettings.background_color || '#FFFFFF',
          background_opacity: navSettings.background_opacity ?? 1.0,
          text_theme: navSettings.text_theme || 'dark',
          login_enabled: navSettings.login_enabled ?? true,
          login_url: navSettings.login_url || '/member/login',
          login_menu_links: Array.isArray(parsedLinks) ? parsedLinks : [],
        };
        setNavigationSettings(resolvedNavSettings);
        setCachedNavSettings(pick(resolvedNavSettings, FLICKER_CACHED_NAV_KEYS));
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  // Apply theme colors and fonts to CSS variables
  useEffect(() => {
    if (settings.theme_primary_color) {
      setThemeColor('primary', settings.theme_primary_color);
    }
    if (settings.theme_accent_color) {
      setThemeColor('accent', settings.theme_accent_color);
    }
    if (settings.theme_highlight_color) {
      setThemeColor('highlight', settings.theme_highlight_color);
    }
    if (settings.theme_neutral_color) {
      setThemeColor('neutral', settings.theme_neutral_color);
    }
    if (settings.theme_border_radius) {
      document.documentElement.style.setProperty('--border-radius', `${settings.theme_border_radius}px`);
    }
    if (settings.theme_heading_font) {
      document.documentElement.style.setProperty('--font-heading', `'${settings.theme_heading_font}', serif`);
    }
    if (settings.theme_body_font) {
      document.documentElement.style.setProperty('--font-body', `'${settings.theme_body_font}', sans-serif`);
    }
  }, [settings]);

  // Update document title and meta tags
  useEffect(() => {
    if (settings.meta_title) {
      document.title = settings.meta_title;
    }

    // Update favicon if set
    if (settings.favicon_url) {
      const favicon = document.querySelector('link[rel="icon"]');
      if (favicon) {
        favicon.href = settings.favicon_url;
      }
    }

    // Update meta description
    if (settings.meta_description) {
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.name = 'description';
        document.head.appendChild(metaDesc);
      }
      metaDesc.content = settings.meta_description;
    }
  }, [settings.meta_title, settings.meta_description, settings.favicon_url]);

  // Robots meta tag
  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'robots';
      document.head.appendChild(meta);
    }
    meta.content = settings.enable_robots ? 'index, follow' : 'noindex, nofollow';
  }, [settings.enable_robots]);

  // Google Analytics
  useEffect(() => {
    const id = settings.google_analytics_id;
    if (!id) return;

    // Avoid injecting duplicate scripts
    if (document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${id}"]`)) return;

    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
    script.async = true;
    document.head.appendChild(script);

    const inline = document.createElement('script');
    inline.text = `if (window.location.pathname.startsWith('/admin')) {window['ga-disable-${id}'] = true;}window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${id}');`;
    document.head.appendChild(inline);

    return () => {
      script.remove();
      inline.remove();
    };
  }, [settings.google_analytics_id]);

  // Google Search Console verification
  useEffect(() => {
    const id = settings.google_search_console_id;
    if (!id) return;

    let meta = document.querySelector('meta[name="google-site-verification"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'google-site-verification';
      document.head.appendChild(meta);
    }
    meta.content = id;

    return () => {
      meta.remove();
    };
  }, [settings.google_search_console_id]);

  const value = {
    settings,
    navigationSettings,
    loading,
    error,
    refetch: loadSettings,
  };

  const recaptchaKey = settings.recaptcha_site_key;
  const recaptchaActive = settings.recaptcha_enabled && recaptchaKey;

  return (
    <SettingsContext.Provider value={value}>
      {recaptchaActive ? (
        <GoogleReCaptchaProvider reCaptchaKey={recaptchaKey}>
          <RecaptchaBridge>
            {children}
          </RecaptchaBridge>
        </GoogleReCaptchaProvider>
      ) : (
        children
      )}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}

export default SettingsContext;
